"use client";

import Link from "next/link";
import Image from "next/image";
import type { CSSProperties } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { EventSummary } from "@project-x/api-clients";
import { createEventSlug } from "@/utils/eventSlug";
import { useLocale, useTranslations } from "next-intl";

export interface EventCarouselProps {
  events: EventSummary[];
}

/** Time between automatic advances when multiple slides exist (ms). */
const CAROUSEL_AUTO_ADVANCE_MS = 20_000;

/**
 * Format a date string to a readable format in Spanish.
 */
const resolveLocale = (locale: string): string => (locale === "en" ? "en-US" : "es-AR");

const formatDate = (value: string, locale: string): string => {
  try {
    const formatted = new Intl.DateTimeFormat(locale, {
      weekday: "long",
      day: "numeric",
      month: "long",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).formatToParts(new Date(value));

    return formatted
      .map((part) => {
        if (part.type === "weekday" || part.type === "month") {
          return part.value.charAt(0).toUpperCase() + part.value.slice(1);
        }
        return part.value;
      })
      .join("");
  } catch (error) {
    return value;
  }
};

const IMAGE_CDN = process.env.IMAGE_CDN_URL || "";

/**
 * Returns the hero image URL for an event from its summary.
 * Uses CDN URL when canonical_object_key is available.
 */
const getHeroImageUrl = (event: EventSummary): string | null => {
  if (!event.hero) return null;
  if (IMAGE_CDN && event.hero.canonical_object_key) {
    return `${IMAGE_CDN}/${event.hero.canonical_object_key}`;
  }
  return `/api/events/${event.id}/assets/${event.hero.id}/image`;
};

/** Upper bound for fitted title (px computed from root font size). */
const CAROUSEL_IMAGE_SIZES =
  "(max-width: 767px) 100vw, (max-width: 1200px) 92vw, 1200px";

type CarouselSlidePanelProps = {
  event: EventSummary;
  locale: string;
  t: ReturnType<typeof useTranslations>;
  prioritized: boolean;
};

function CarouselSlidePanel({ event, locale, t, prioritized }: CarouselSlidePanelProps) {
  const heroImageUrl = getHeroImageUrl(event);
  const slug = createEventSlug(event);
  const formattedDate = formatDate(event.starts_at, locale);
  const venue = [event.venue?.name, event.venue?.city, event.venue?.state].filter(Boolean).join(" · ");

  return (
    <div className="gruv-carousel__slide">
      {heroImageUrl ? (
        <Image
          src={heroImageUrl}
          alt=""
          aria-hidden="true"
          fill
          unoptimized
          sizes={CAROUSEL_IMAGE_SIZES}
          priority={prioritized}
          fetchPriority={prioritized ? "high" : "auto"}
          loading={prioritized ? "eager" : "lazy"}
          className="gruv-carousel__slide-image"
        />
      ) : null}
      <div className="gruv-carousel__image-overlay" aria-hidden="true" />
      <div className="gruv-carousel__frame-inner">
        <div className="gruv-carousel__content">
          {!heroImageUrl && <div className="gruv-carousel__backdrop" aria-hidden="true" />}
          <div className="gruv-carousel__details">
            <span className="gruv-carousel__eyebrow">{t("carousel.eyebrow")}</span>
            <div className="gruv-carousel__title-wrap">
              <h2 className="gruv-carousel__title">{event.name}</h2>
            </div>
            <p className="gruv-carousel__description">
              {event.description ?? t("carousel.fallbackDescription")}
            </p>
            <p className="gruv-carousel__date">{formattedDate}</p>
            {venue ? <p className="gruv-carousel__venue">{venue}</p> : null}
            <Link
              href={`/events/${slug}`}
              prefetch={false}
              className="gruv-carousel__cta"
              aria-label={t("carousel.aria.cta", { name: event.name })}
            >
              {t("carousel.cta")}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * EventCarousel displays the top popular events in a horizontal track with smooth sliding
 * and auto-advance every {@link CAROUSEL_AUTO_ADVANCE_MS} when there is more than one slide.
 */
export function EventCarousel({ events }: EventCarouselProps) {
  const t = useTranslations("events");
  const locale = resolveLocale(useLocale());
  const [activeIndex, setActiveIndex] = useState(0);
  const count = events.length;
  const canNavigate = count > 1;

  const next = useCallback(() => {
    if (!canNavigate) {
      return;
    }
    setActiveIndex((index) => {
      return (index + 1) % count;
    });
  }, [canNavigate, count]);

  const prev = useCallback(() => {
    if (!canNavigate) {
      return;
    }
    setActiveIndex((index) => {
      return (index - 1 + count) % count;
    });
  }, [canNavigate, count]);

  const indicators = useMemo(
    () =>
      events.map((event, index) => ({
        id: event.id,
        label: event.name,
        index,
        active: index === activeIndex,
      })),
    [events, activeIndex],
  );

  /* Restart the 20s timer whenever the active slide changes (manual, arrow, or auto). */
  useEffect(() => {
    if (!canNavigate) {
      return;
    }

    let intervalId: ReturnType<typeof setInterval> | null = null;

    const clearTimer = () => {
      if (intervalId !== null) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    const armTimer = () => {
      clearTimer();
      if (typeof document !== "undefined" && document.visibilityState === "hidden") {
        return;
      }
      intervalId = setInterval(() => {
        setActiveIndex((index) => (index + 1) % count);
      }, CAROUSEL_AUTO_ADVANCE_MS);
    };

    armTimer();

    const onVisibility = () => {
      if (typeof document === "undefined") {
        return;
      }
      if (document.visibilityState === "hidden") {
        clearTimer();
      } else {
        armTimer();
      }
    };

    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      clearTimer();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [canNavigate, count, activeIndex]);

  if (count === 0) {
    return null;
  }

  const slidesStyle: CSSProperties = {
    ["--carousel-count" as string]: count,
    transform: `translateX(calc(-100% * ${activeIndex} / ${count}))`,
  };

  return (
    <section className="gruv-carousel" aria-label={t("carousel.aria.section")}>
      <div className="gruv-carousel__frame">
        <div className="gruv-carousel__slides" style={slidesStyle}>
          {events.map((event, index) => (
            <CarouselSlidePanel
              key={event.id}
              event={event}
              locale={locale}
              t={t}
              prioritized={index === 0}
            />
          ))}
        </div>
        {canNavigate ? (
          <div className="gruv-carousel__controls">
            <button type="button" className="gruv-carousel__control-btn" onClick={prev} aria-label={t("carousel.aria.prev")}>
              ←
            </button>
            <button type="button" className="gruv-carousel__control-btn" onClick={next} aria-label={t("carousel.aria.next")}>
              →
            </button>
          </div>
        ) : null}
      </div>
      {canNavigate ? (
        <div className="gruv-carousel__indicators" role="tablist" aria-label={t("carousel.aria.indicators")}>
          {indicators.map((indicator) => (
            <button
              key={indicator.id}
              type="button"
              role="tab"
              aria-selected={indicator.active}
              className={indicator.active ? "gruv-carousel__indicator gruv-carousel__indicator--active" : "gruv-carousel__indicator"}
              onClick={() => setActiveIndex(indicator.index)}
            >
              <span>{indicator.label}</span>
            </button>
          ))}
        </div>
      ) : null}
    </section>
  );
}
